import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon, icon } from "@msuite/ui/icons";
import { LazyFallback } from "@msuite/ui/loading-screen";
import { useTranslation } from "@msuite/ui/localization";
import { createStandardMenuItems, Menu } from "@msuite/ui/menu";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import type { NoteWithFiles } from "@msuite/utils/types";
import React, { lazy, Suspense } from "react";
import { client } from "@/hono";
import { useAuth } from "@/hooks/use-auth";
import { useCitadel } from "@/hooks/use-citadel";
import { useNoteCapabilities } from "@/hooks/use-note-capabilities";
import { EditNoteModal } from "@/modals/notes/edit-note-modal";

const AuditModal = lazy(() => import("@/modals/audit-modal"));

/** Props Interface */
interface NoteMenuProps {
	handleOnMarkAsReply: () => void;
	onCompleteEdit: () => void | Promise<void>;
	note: NoteWithFiles;
	/** Whether the caller may write a note here at all — a reply is a note like any other. */
	canReply: boolean;
	withIsImportant?: boolean;
	isImportantLabel?: string;
	withIsImportantIsDisabled?: boolean;
}

export function NoteMenu({
	handleOnMarkAsReply,
	onCompleteEdit,
	note,
	canReply,
	withIsImportant,
	isImportantLabel,
	withIsImportantIsDisabled = false,
}: NoteMenuProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { citadel } = useCitadel();
	const { user } = useAuth();
	const editNoteModal = useDisclosure();
	const auditModal = useDisclosure();
	const controller = useAsyncModal();
	const notes = useNoteCapabilities();

	/** Computed */
	// `GET /audit` reaches the history, and `Notes.authorizeNoteParent` lets the author through
	// before it asks any rule — so a `read Note` test alone drops the entry for every member's own
	// note. Someone else's note under a readable parent still resolves server-side only.
	const canReadHistory = note.createdBy === user?.id || citadel?.can("read", "Note", note) === true;

	/** Functions */
	async function handleOnDelete() {
		try {
			const isConfirmed = await controller.confirm();
			if (!isConfirmed) return;
			await client.notes[":noteId"].$delete({ param: { noteId: note.id } });
			await onCompleteEdit();
		} catch (error) {
			console.error(error);
		}
	}

	async function handleOnEjectFromThread() {
		try {
			await client.notes[":noteId"].$patch({
				param: {
					noteId: note.id,
				},
				json: {
					note: note.note,
					parentNoteId: undefined,
				},
			});
			await onCompleteEdit();
		} catch (error) {
			console.error(error);
		}
	}

	/** Computed */
	// Both rules reach only what the caller wrote, so the checks are bound to this note rather than
	// to the category: a category check would answer whether the caller may edit *a* note.
	const canUpdateNote = notes.canUpdate(note);
	const canDeleteNote = notes.canDelete(note);

	/** Render */
	return (
		<React.Fragment>
			<Menu
				items={createStandardMenuItems({
					onEdit: canUpdateNote ? editNoteModal.onOpen : undefined,
					onDelete: canDeleteNote ? handleOnDelete : undefined,
					items: [
						// Replying opens the note form, so it asks what the form asks: the caller's
						// own rule and the parent's read right. Offered without both, it marks a
						// note and then has nothing to write into.
						canReply
							? {
									label: t("notes.reply"),
									icon: icon("MessageSquareText"),
									onClick: handleOnMarkAsReply,
								}
							: null,
						canReadHistory
							? {
									label: t("general.history"),
									icon: icon("History"),
									onClick: auditModal.onOpen,
								}
							: null,
						// Taking a note out of its thread is a correction of that note, so it
						// follows the same rule as editing it.
						note.parentNoteId && canUpdateNote
							? {
									label: t("notes.unmark_as_reply"),
									icon: icon("Split"),
									onClick: handleOnEjectFromThread,
								}
							: null,
					],
				})}
			>
				<div className="-my-2">
					<Button
						icon={<Icon name="EllipsisVertical" />}
						variant="ghost"
						size="xxs"
					/>
				</div>
			</Menu>
			{editNoteModal.isOpen && (
				<EditNoteModal
					isOpen={editNoteModal.isOpen}
					onClose={editNoteModal.onClose}
					note={note}
					onComplete={onCompleteEdit}
					withIsImportant={withIsImportant}
					isImportantLabel={isImportantLabel}
					withIsImportantIsDisabled={withIsImportantIsDisabled}
				/>
			)}
			<Suspense fallback={<LazyFallback variant="portal" />}>
				{auditModal.isOpen && (
					<AuditModal
						isOpen={auditModal.isOpen}
						onClose={auditModal.onClose}
						auditTable="notesAudit"
						queryKey={["notesAudit", note.id]}
						identifier={{
							key: "noteId",
							value: note.id,
						}}
					/>
				)}
			</Suspense>
			<ConfirmModal
				message={t("notes.delete_confirmation_message")}
				controller={controller}
			/>
		</React.Fragment>
	);
}
